import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronRight, ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";

export default function ArticlePage() {
  const { slug } = useParams();
  const { t } = useI18n();
  const [article, setArticle] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api
      .get(`/articles/${slug}`)
      .then((r) => {
        setArticle(r.data);
        document.title = r.data.meta_title || `${r.data.title} — BioNutrix Journal`;
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-24 text-center">
        <h1 className="font-display text-4xl text-brand mb-4">Articolo non trovato</h1>
        <Link to="/journal" className="link-underline text-brand">Torna al Journal</Link>
      </div>
    );
  }
  if (!article) {
    return <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-24 text-muted-ink">{t("common.loading")}</div>;
  }

  const dateStr = article.published_at
    ? new Date(article.published_at).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" })
    : "";

  return (
    <article className="pb-16" data-testid="article-page">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10 pt-8">
        <nav className="flex items-center gap-1.5 text-xs text-muted-ink" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-brand">Home</Link>
          <ChevronRight className="h-3 w-3" />
          <Link to="/journal" className="hover:text-brand">Journal</Link>
          <ChevronRight className="h-3 w-3" />
          <span className="text-brand">{article.title}</span>
        </nav>
      </div>

      <header className="mx-auto max-w-3xl px-6 lg:px-10 py-12 text-center">
        <div className="text-xs uppercase tracking-editorial text-muted-ink mb-4">
          {article.category} · {article.read_time} min · {dateStr}
        </div>
        <h1 className="font-display text-4xl md:text-5xl lg:text-6xl text-brand leading-[1.05]">
          {article.title}
        </h1>
        <p className="mt-6 text-lg text-muted-ink">{article.excerpt}</p>
      </header>

      <div className="mx-auto max-w-5xl px-6 lg:px-10">
        <div className="aspect-[16/9] overflow-hidden bg-cream-warm">
          <img src={article.image} alt={article.title} className="w-full h-full object-cover" />
        </div>
      </div>

      <div
        className="mx-auto max-w-3xl px-6 lg:px-10 pt-12 prose-article"
        dangerouslySetInnerHTML={{ __html: article.content }}
      />

      <div className="mx-auto max-w-3xl px-6 lg:px-10 mt-8 border-t border-soft pt-6">
        <p className="text-xs text-muted-ink italic">
          {article.author} · {dateStr}
        </p>
      </div>

      {article.related?.length > 0 && (
        <section className="mx-auto max-w-[1400px] px-6 lg:px-10 py-16">
          <h2 className="font-display text-3xl text-brand mb-8">Continua a leggere</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {article.related.map((r) => (
              <Link key={r.slug} to={`/journal/${r.slug}`} className="group">
                <div className="image-hover-scale aspect-[4/3] overflow-hidden bg-cream-warm mb-4">
                  <img src={r.image} alt={r.title} className="w-full h-full object-cover" />
                </div>
                <div className="text-[10px] uppercase tracking-editorial text-muted-ink mb-2">
                  {r.category}
                </div>
                <h3 className="font-display text-xl text-brand leading-tight">{r.title}</h3>
                <div className="mt-2 inline-flex items-center gap-1 text-sm text-brand link-underline">
                  {t("common.read_more")} <ArrowUpRight className="h-3 w-3" />
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <style>{`
        .prose-article p { margin: 1em 0; line-height: 1.75; color: var(--text-primary); font-size: 1.05rem; }
        .prose-article h2 { font-family: 'Cormorant Garamond', Georgia, serif; font-size: 1.75rem; margin: 2em 0 0.5em; color: var(--brand); }
        .prose-article h3 { font-family: 'Cormorant Garamond', Georgia, serif; font-size: 1.35rem; margin: 1.5em 0 0.5em; color: var(--brand); }
        .prose-article em { color: var(--text-secondary); font-style: italic; }
        .prose-article ul { padding-left: 1.25em; }
        .prose-article ul li { list-style: disc; margin: 0.5em 0; }
      `}</style>
    </article>
  );
}
